import type { Game } from '@game/types/Game'
import { useClock, useDayjs, useI18n, useTimeoutFn, useCountdown, ref, type Ref, computed } from '#imports'

export const useGameOpeningHours = (game: Ref<Game | undefined>) => {
    const { t } = useI18n()
    const { clock } = useClock()
    const dayjs = useDayjs()

    const isOpen: Ref<boolean> = ref(game.value?.opening_hours.is_open ?? false)

    const countdown = import.meta.client
        ? useCountdown(0, { onComplete() { isOpen.value = true } })
        : null

    const remaining = computed(() => countdown?.remaining.value ?? 0)

    const opensIn = computed<string>(() => {
        if (remaining.value <= 0) return ''

        const minutes = (remaining.value / 60) | 0
        const seconds = (remaining.value % 60) | 0

        return `${t('game.gameTile.opens_in')} ${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`
    })

    const opensAt = computed<string | null>(() => {
        if (isOpen.value) return null

        const opensAt = dayjs(game.value?.opening_hours.opens_at).tz().format('LT')

        return `${t('game.gameTile.opens_at')} ${opensAt}`
    })
    const opensSoon = computed<string | boolean | null>(() => {
        if (isOpen.value) return null

        const opensAt = dayjs(game.value?.opening_hours.opens_at ?? 0).tz()
        const serverTime = dayjs(clock.value).tz()

        return opensAt.diff(serverTime) < dayjs.duration({ minutes: 30 }).asMilliseconds()
    })

    let stopScheduled: (() => void) | undefined

    function computeOpeningHours() {
        if (game.value?.opening_hours.opens_at) {
            isOpen.value = false

            const timeRemaining = getSecondsRemaining()
            const halfAnHour = 1800 // Half an hour in seconds

            if (import.meta.client) {
                if (timeRemaining >= 0 && timeRemaining <= halfAnHour) {
                    countdown?.start(timeRemaining)

                    return
                }

                stopScheduled?.()
                const { stop } = useTimeoutFn(() => computeOpeningHours(), (timeRemaining - halfAnHour) * 1000)
                stopScheduled = stop
            }
        }

        return false
    }

    function getSecondsRemaining(): number {
        const gameOpeningDate = game.value?.opening_hours.opens_at ?? null
        if (!gameOpeningDate) return 0

        const opensAt = dayjs(gameOpeningDate).tz()
        if (!opensAt) return 0

        const serverTime = dayjs(clock.value).tz()

        return opensAt.diff(serverTime, 'seconds')
    }

    // --- Opening and playability
    const isClosed = computed((): (string | boolean | null) => {
        if (!isOpen.value) return opensSoon.value ? opensIn.value : opensAt.value

        return false
    })

    const isDisabled = computed((): boolean => {
        return !game.value?.is_active || !!isClosed.value // to use as a truthy value since isClosed returns either a string or false
    })

    const isPlayable = computed((): boolean => {
        //  TODO: Staff only - games are playable by staff users
        return !isClosed.value && !isDisabled.value
    })

    return {
        isOpen,
        opensAt,
        opensSoon,
        opensIn,
        computeOpeningHours,

        isClosed,
        isDisabled,
        isPlayable,
    }
}
